import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import styles from "./LimitsCard.module.css";
import { TabPanel, TabView } from "primereact/tabview";
import * as client from "../../../client";
import { useContext, useState } from "react";
import { cloneDeep } from "lodash";
import { ThemeContext } from "../../../themes/Theme";
import { Button } from "primereact/button";
import { testDeductibleOrLimitFunction } from "../../../api/routes";
import { useToast } from "../../../context-providers/Toast";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { readableNameFromPeril } from "../general-input/util";
import { useUpdateReport } from "../data";
import { EditableFunctionBlock } from "../EditableFunctionBlock";
import { FunctionInstructions } from "../FunctionInstructions";
import { FunctionCardTitle } from "../FunctionRegenIndicator";

interface LimitsCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

export const LimitsCard: React.FC<LimitsCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const limitInfo = specialtyPropertyInfo.report_json.limit_info;
  const extractedLimits = limitInfo?.extracted_limits ?? {};
  const manualLimits = limitInfo?.manual_limits ?? {};
  const manualPerils = Object.keys(manualLimits);
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const toast = useToast();
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  const filenames = Object.keys(extractedLimits);
  const selectedFormName =
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name;
  const initialIndex =
    selectedFormName && filenames.includes(selectedFormName)
      ? filenames.indexOf(selectedFormName)
      : 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  // Persist (or clear, when override is null) the hand-edited function for one
  // peril. Clones only the limit_info subtree and sends it back whole; the
  // generated executable_python_function_str is left untouched. For a manual
  // override, stamp the form it's authored against so cat modeling only blocks
  // once the report later switches forms (see report_has_stale_override).
  const applyOverride = async (
    filename: string | null,
    peril: client.peril_type,
    override: string | null
  ): Promise<void> => {
    if (!limitInfo) return;
    const next = cloneDeep(limitInfo);
    const entry =
      filename === null
        ? next.manual_limits[peril]
        : next.extracted_limits[filename]?.[peril];
    if (!entry) return;
    entry.executable_python_function_override = override;
    // Only manual overrides (filename === null) can go stale — extracted ones are
    // scoped to their form's bucket — so only they carry the authored-form stamp.
    // Clearing the override clears the stamp.
    if (filename === null) {
      entry.override_authored_against_form =
        override === null ? null : (selectedFormName ?? null);
    }
    const readablePerilName = readableNameFromPeril(peril);
    try {
      await updateReport.mutateAsync({ advancedLimitInfo: next });
      toast.show({
        severity: "success",
        summary:
          override === null
            ? "Reverted to Generated Function"
            : "Limit Function Saved",
        detail:
          override === null
            ? `The ${readablePerilName} function was reverted to the generated version.`
            : `The ${readablePerilName} function override was saved.`,
      });
    } catch (e) {
      toast.show({
        severity: "error",
        summary: "Save Failed",
        detail: `Could not update the ${readablePerilName} function.`,
      });
      throw e;
    }
  };

  const renderPeril = (
    filename: string | null,
    peril: string,
    advancedLimit: client.AdvancedLimit
  ) => {
    const perilType = peril as client.peril_type;
    const readablePerilName = readableNameFromPeril(perilType);
    const effectiveFunction =
      advancedLimit.executable_python_function_override ??
      advancedLimit.executable_python_function_str;
    const hasOverride =
      advancedLimit.executable_python_function_override != null;
    const hasFunction = effectiveFunction !== "";
    const quotationTooltip = advancedLimit.document_quotation ? (
      <TooltipWrapper
        tooltipText={advancedLimit.document_quotation}
        tooltipProps={{ position: "left" }}
      >
        <i className="pi pi-info-circle" />
      </TooltipWrapper>
    ) : null;

    if (!hasFunction) {
      return (
        <div className={styles.verticalContainerSmall} key={peril}>
          <div className={styles.horizontalContainer}>
            <span className={styles.header}>{readablePerilName}</span>
            {quotationTooltip}
          </div>
          <span style={{ fontSize: "14px" }}>
            {advancedLimit.short_limit_description}
          </span>
          <span
            style={{
              fontSize: "13px",
              fontStyle: "italic",
              color: "var(--text-color-secondary)",
            }}
          >
            Function will be generated on next rerun.
          </span>
        </div>
      );
    }

    return (
      <EditableFunctionBlock
        key={peril}
        readablePerilName={readablePerilName}
        description={advancedLimit.short_limit_description}
        headerExtra={quotationTooltip}
        testButton={
          <Button
            label="Test"
            outlined
            size="small"
            style={{ padding: "4px 6px" }}
            onClick={async () => {
              try {
                const result = await testDeductibleOrLimitFunction(
                  specialtyPropertyInfo.report_id,
                  filename,
                  perilType,
                  "limit"
                );
                toast.show({
                  severity: "success",
                  summary: "Limit Function Success",
                  detail: `The ${readablePerilName} function ran successfully on ${result.num_addresses_run} addresses.`,
                });
              } catch {
                toast.show({
                  severity: "error",
                  summary: "Limit Function Failure",
                  detail: `Something went wrong when running the ${readablePerilName} function.`,
                });
              }
            }}
          />
        }
        functionStr={effectiveFunction}
        hasOverride={hasOverride}
        isLightMode={isLightMode}
        isSaving={updateReport.isPending}
        onSave={(nextOverride) =>
          applyOverride(filename, perilType, nextOverride)
        }
        onRevert={() => applyOverride(filename, perilType, null)}
      />
    );
  };

  return (
    <Card
      title={
        <FunctionCardTitle
          title="Limits"
          reportId={specialtyPropertyInfo.report_id}
        />
      }
    >
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        {Object.entries(extractedLimits).map(
          ([filename, extractedLimitsForFile]) => {
            return (
              <TabPanel header={filename} key={filename}>
                <div className={styles.verticalContainer}>
                  <FunctionInstructions
                    reportId={specialtyPropertyInfo.report_id}
                    filename={filename}
                    functionType="limit"
                    instructions={
                      limitInfo?.extraction_instructions?.[filename]
                    }
                  />
                  {Object.entries(extractedLimitsForFile).map(
                    ([peril, advancedLimit]) =>
                      renderPeril(filename, peril, advancedLimit)
                  )}
                </div>
              </TabPanel>
            );
          }
        )}
        {manualPerils.length > 0 && (
          <TabPanel header="Manual">
            <div className={styles.verticalContainer}>
              {Object.entries(manualLimits).map(([peril, advancedLimit]) =>
                renderPeril(null, peril, advancedLimit)
              )}
            </div>
          </TabPanel>
        )}
      </TabView>
    </Card>
  );
};
